/**
 * This is the landing page that the Azure Marketplace sends Subscribers to after
 * they have purchased a plan.
 */

import React, { useEffect, useState } from 'react';
import { useForm, FieldValues } from 'react-hook-form';
import { useLocation, useNavigate, Link } from 'react-router-dom';

import _ from 'lodash';
import axios, { AxiosError, AxiosResponse } from 'axios';

import { useMicrosoft } from '../../_libs/hooks/useMicrosoft';

import { StatusMessageType } from '../../atoms/Message';
import Table from '../../atoms/Table';
import TableBody from '../../atoms/TableBody';
import TableData from '../../atoms/TableData';
import TableHead from '../../atoms/TableHead';
import TableHeader from '../../atoms/TableHeader';
import TableRow from '../../atoms/TableRow';
import MicrosoftSSOWidget from '../../molecules/MicrosoftSSOWidget';
import StandardPageTemplate from '../../templates/StandardPage';

import { ResolveResponse } from './types';

interface ConfirmSubscriptionProps {
  subscriptionId: string;
  contactEmail: string;
  contactPhone: string;
  graphUserPrincipalName: string;
  graphUserId: string;
}

/**
 * Send a resolve request to the Microsoft Subscription API. 
 * Update subscription data on the landing page and a status message
 * based on the response.
 * @param purchaseToken 
 * @param setSubscription 
 */
async function resolvePurchaseToken (
  purchaseToken: string,
  setSubscription: (subscription: any) => void,
  setStatusMessage: (options: StatusMessageType) => void,
) {
  axios
    .post(
      '/apis/microsoft/subscriptions/resolve/',
      {
        purchaseToken,
      }
    ).then(
      (res: AxiosResponse) => {
        if (res.data.status === 'OK') {
          if (res.data.subscription) {
            setSubscription(res.data.subscription);
            setStatusMessage({
              type: 'success',
              message: 'Subscription successfully loaded.',
            });
          } else {
            // TODO: Sentry
            console.error('<SubscriberLandingPage>: Resolve response was OK but no subscription returned.');
          }
        } else {
          setStatusMessage({
            type: 'error',
            message: 'Unable to load Subscription data.',
          });
          // TODO: Sentry
        }
      },
      (err: AxiosError) => {
        // TODO: Sentry
        console.error('<SubscriberLandingPage>', err);
        setStatusMessage({
          type: 'error',
          message: 'Unable to verify and load Subscription data.',
        });
      },
    );
}

/**
 * After the user confirms the subscription data and their contact information,
 * send this data to the backend for parsing
 * 
 * @param opts 
 * @param setSubscription 
 */
function confirmSubscriptionData (opts: ConfirmSubscriptionProps, setSubscription: (subscription: any) => void) {
  axios
    .post(
      '/apis/microsoft/subscriptions/confirm/',
      opts,
    ).then(
      (res: AxiosResponse) => {
        if (res.data.status === 'OK') {
          if (res.data.subscription) {
            setSubscription(true);
          } else {
            console.error('<SubscriberLandingPage>: Confirm response was OK but no subscription returned.');
          }
        }
      },
      (err: AxiosError) => {
        console.error('<SubscriberLandingPage>', err);
      },
    );
}

const SubscriberTableRow = ({
  name,
  data
}): JSX.Element => {
  return (
    <TableRow
      className="f-medium"
      style={{
        lineHeight: '2rem',
      }}
    >
      <TableData><strong>{name}</strong></TableData>
      <TableData>{data}</TableData>
    </TableRow>
  );
};

const SubscriberLandingPage = (): JSX.Element => {
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm();
  const {
    graphData,
    setPurchaseToken,
    purchaseToken,
    planData,
    fetchPlanData,
    resetPlanData,
    isAuthenticated,
    error: useMicrosoftError,
  } = useMicrosoft();
  const navigate = useNavigate();
  const { search } = useLocation();
  const [resolveResponse, setResolveResponse] = useState<ResolveResponse>();
  const [statusMessage, setStatusMessage] = useState<StatusMessageType>();
  const [confirmed, setConfirmed] = useState(false);
  const [contactEmail, setContactEmail] = useState<string>();
  const [contactPhone, setContactPhone] = useState<string>();
  
  const query = new URLSearchParams(search);

  useEffect(() => {
    if (query.get('token')) {
      setPurchaseToken(query.get('token'));
    }
  }, []);

  useEffect(() => {
    if (graphData) {
      setContactEmail(graphData.mail);
      setContactPhone(graphData.mobilePhone || graphData.businessPhones[0]);
    }
  }, [graphData]);

  useEffect(() => {
    if (purchaseToken && graphData && !resolveResponse) {
      resolvePurchaseToken(purchaseToken.token, setResolveResponse, setStatusMessage);
    }
  }, [purchaseToken, graphData]);

  useEffect(() => {
    if (resolveResponse) {
      fetchPlanData(resolveResponse.subscription.id);
    }
    return () => {
      resetPlanData();
    };
  }, [resolveResponse]);

  // Subscriber action
  function handleConfirmation (data: FieldValues) {
    const opts = {
      subscriptionId: resolveResponse.subscription.id,
      graphUserPrincipalName: data.graphUserPrincipalName,
      graphUserId: data.graphUserId,
      contactEmail: data.contactEmail,
      contactPhone: data.contactPhone,
    };
    confirmSubscriptionData(opts, setConfirmed);
  }

  if (!isAuthenticated) {
    return (
      <StandardPageTemplate>
        <MicrosoftSSOWidget error={useMicrosoftError} type={'subscriber'} />
      </StandardPageTemplate>
    );
  } else if (confirmed) {
    return (
      <StandardPageTemplate>
        <h1 className="f-serif f-normal f-italic">
          Thank you!
        </h1>
        <p>
          Thank you for submitting your subscription for activation. A representative from Lightbeam will reach out in 1-3 days to activate your account.
        </p>
        <p>
          Please watch for an email or call from us to discuss next steps.
        </p>
        <Link className="button td-none" to="/microsoft/subscriber/subscriptions/">View All Subscriptions</Link>
      </StandardPageTemplate>
    );
  } else {
    if (!purchaseToken) {
      return (
        <StandardPageTemplate>
          <h1 className="f-serif f-normal f-italic">
            Error pulling up this subscription.
          </h1>
          <p>
            We couldn't identify this purchase. Please reopen this SaaS subscription in the Azure portal or in Microsoft 365 Admin Center and select "Configure Account" or "Manage Account" again.
          </p>
          <Link className="button td-none" to="/microsoft/subscriber/subscriptions/">View All Subscriptions</Link>
        </StandardPageTemplate>
      );
    } else {
      if (resolveResponse) {
        const subscription = resolveResponse.subscription;
        const plan = _.find(planData, { planId: subscription.planId });
        const planName = (plan) ? plan.displayName : subscription.planId;
        const planDescription = (plan) ? plan.description : 'Description did not load.';
        if (subscription.saasSubscriptionStatus === 'Subscribed') {
          navigate(`/microsoft/subscriber/details/${subscription.id}/`);
        } else {        
          return (
            <StandardPageTemplate>
              <h1 className="f-serif f-normal f-italic">
                Thank you for your subscription
              </h1>
              <p>
                Verify the data below before in order to submit your subscription for activation by Lightbeam Health Solutions, Inc.
              </p>
              <br />
              <form onSubmit={handleSubmit(handleConfirmation)}>
                <h2 className="f-serif f-normal">1. Confirm Subscription Information</h2>
                <p>If the information below is not correct, close this page and update your settings in the Azure Microsoft Marketplace.</p>  
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableHeader>Property</TableHeader>
                      <TableHeader>Information Provided</TableHeader>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    <SubscriberTableRow name="Graph User" data={graphData?.mail} />
                    <SubscriberTableRow name="Graph User ID" data={graphData?.id} />
                    <input type="hidden" {...register('graphUserPrincipalName', {
                      value: graphData?.userPrincipalName,
                    })} />
                    <input type="hidden" {...register('graphUserId', {
                      value: graphData?.id,
                    })} />
                    <SubscriberTableRow name="Beneficiary Email" data={subscription.beneficiary.emailId} />
                    <SubscriberTableRow name="Subscription Name" data={subscription.name ? subscription.name : <em>No name given.</em>} />
                    <SubscriberTableRow name="Plan Name" data={planName} />
                    <SubscriberTableRow name="Plan Description" data={planDescription} />
                    <SubscriberTableRow name="Subscription Status" data={subscription.saasSubscriptionStatus} />
                    <SubscriberTableRow name="Subscription ID" data={subscription.id} />
                  </TableBody>
                </Table>
                <br />
                <br />
                <h2 className="f-serif f-normal">2. Confirm Contact Information</h2>
                <p>This is the contact information we will use to contact you about your subscription before we activate it. Please ensure the information below is correct.</p>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableHeader>Contact Piece</TableHeader>
                      <TableHeader>Value</TableHeader>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    <TableRow className="f-medium">
                      <TableData>
                        <label htmlFor="contactEmail">
                          <strong>Email</strong> (required)
                        </label>
                      </TableData>
                      <TableData>
                        <input 
                          style={{
                            lineHeight: '2rem',
                            paddingTop: 0,
                            paddingLeft: '0.5rem',
                            paddingBottom: 0,
                            paddingRight: '0.5rem',
                            width: '100%',
                          }}
                          id="contactEmail"
                          {...register('contactEmail', {
                            value: contactEmail || graphData?.mail,
                            required: 'Contact Email is required.',
                            pattern: {
                              value: /\S+@\S+\.\S+/,
                              message: 'Value entered does not match email format (e.g. address@domain.io).'
                            },
                          })}
                        />
                        { errors?.contactEmail &&
                          <p>{errors.contactEmail.message.toString()}</p>
                        }
                      </TableData>
                    </TableRow>
                    <TableRow className="f-medium">
                      <TableData>
                        <label htmlFor="contactPhone">
                          <strong>Phone Number</strong> (required)
                        </label>
                      </TableData>
                      <TableData>
                        <input 
                          style={{
                            lineHeight: '2rem',
                            paddingTop: 0,
                            paddingLeft: '0.5rem',
                            paddingBottom: 0,
                            paddingRight: '0.5rem',
                            width: '100%',
                          }}
                          id="contactPhone"
                          type="tel"
                          {...register('contactPhone', {
                            value: contactPhone || graphData?.mobilePhone || graphData?.businessPhones[0],
                            required: 'Please enter a 10- or 14-character phone number like 1234567890 or (123) 456-7890.',
                            minLength: {
                              value: 10,
                              message: 'The phone number entered is too short. Please enter a 10- or 14-character phone number like 1234567890 or (123) 456-7890.',
                            }, // 4444444444
                            maxLength: {
                              value: 14,
                              message: 'The phone number entered is too long. Please enter a 10- or 14-character phone number like 1234567890 or (123) 456-7890.',
                            }, // (444) 444-4444
                          })} />
                        { errors?.contactPhone &&
                          <p>{errors.contactPhone.message.toString()}</p>
                        }
                      </TableData>
                    </TableRow>
                  </TableBody>
                </Table>
                <br />
                <br />
                <button type="submit">Confirm Settings</button>
              </form>
            </StandardPageTemplate>
          );
        }
      } else {
        return (
          <StandardPageTemplate>
            Loading subscription data...
          </StandardPageTemplate>
        );
      }
    }
  }
};

export default SubscriberLandingPage;
